// Notifications API client. Shapes mirror the Go backend DTOs in
// handlers/handler_notifications.go so the contract stays aligned.
import { apiFetch } from './config';

export type NotificationCategory =
  | 'analysis_complete'
  | 'tips'
  | 'promotions'
  | 'account'
  | (string & {});

export interface NotificationItem {
  id: string;
  category: NotificationCategory;
  title: string;
  body: string;
  data?: Record<string, unknown>;
  deep_link?: string;
  image_url?: string;
  read: boolean;
  created_at: string;
}

export interface NotificationPreferences {
  push_enabled: boolean;
  analysis_complete: boolean;
  tips: boolean;
  promotions: boolean;
  account: boolean;
  quiet_hours_start: number | null;
  quiet_hours_end: number | null;
}

const browserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return '';
  }
};

export interface DeviceRegistrationResponse {
  success?: boolean;
}

export const registerDevice = (token: string, platform = 'web'): Promise<DeviceRegistrationResponse> =>
  apiFetch<DeviceRegistrationResponse>('devices', {
    method: 'POST',
    body: JSON.stringify({ token, platform, timezone: browserTimezone() }),
  });

export const unregisterDevice = (token: string): Promise<undefined> =>
  apiFetch<undefined>(`devices/${encodeURIComponent(token)}`, { method: 'DELETE' });

export const getNotifications = (limit = 30, offset = 0): Promise<NotificationItem[]> =>
  apiFetch<NotificationItem[]>(`notifications?limit=${limit}&offset=${offset}`);

export const getUnreadCount = (): Promise<{ unread: number }> =>
  apiFetch<{ unread: number }>('notifications/unread-count');

export const markNotificationsRead = (id?: string): Promise<undefined> =>
  apiFetch<undefined>('notifications/read', {
    method: 'POST',
    body: JSON.stringify(id ? { id } : {}),
  });

export const getNotificationPreferences = (): Promise<NotificationPreferences> =>
  apiFetch<NotificationPreferences>('notification-preferences');

export const updateNotificationPreferences = (
  prefs: NotificationPreferences
): Promise<NotificationPreferences> =>
  apiFetch<NotificationPreferences>('notification-preferences', {
    method: 'PUT',
    body: JSON.stringify(prefs),
  });
